import type { CampaignGoalKind } from "../../../skills/leadcue-reply/scripts/lib/campaign-config.ts";
import { BOOKING_CONTEXT_KEY } from "../../../skills/leadcue-reply/scripts/lib/context-contracts.ts";
import {
  dependenciesSatisfied,
  type PlanStep,
  type WorkPlan,
} from "../../../skills/leadcue-reply/scripts/lib/work-plan.ts";
import { goalLifecycle, tracksCompletion } from "./goal-lifecycle.ts";

/*
 * The booking question, answered on the completion card (docs/2026-09-21-inbox-view-design.md,
 * change 6). Drafting asks it when a reply depends on whether a booking exists, and until
 * 2026-09-21 the app drew it as a text box asking for event and calendar IDs. Nothing typed there
 * could succeed: `answerRun` parses the value with the booking schema and a string never parses. So
 * the rep now chooses. A booking in hand is recorded by Mark booked (or its event equivalent), the
 * receipt that already closes every open step; "not yet" records the explicit absence the question
 * itself describes. Whether that answer satisfies the question stays the drafting rule's decision.
 */

/** The open booking question on this plan, or null. Only one step ever carries the key. */
export function openBookingQuestion(plan: WorkPlan): PlanStep | null {
  return (
    plan.steps.find(
      (step) =>
        step.input.kind === "question" &&
        step.input.questions.length === 1 &&
        step.input.questions[0]?.key === BOOKING_CONTEXT_KEY &&
        (step.state === "pending" || step.state === "invalidated") &&
        dependenciesSatisfied(plan, step),
    ) ?? null
  );
}

export type BookingStatusChoices = {
  /** What the card asks, in the goal's own words. */
  question: string;
  /** The button that records the completion, or null where the goal has no receipt. */
  completion: string | null;
  /** The button that records that nothing exists yet. */
  absence: string;
};

const capitalized = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1);

/**
 * The two answers, from the goal lifecycle table: "Booked" and "Not booked yet" for a meeting,
 * "Registered" and "Not registered yet" for an event. A goal with no completion receipt is offered
 * no completion at all, only the absence, and is asked about scheduling rather than a booking.
 */
export function bookingStatusChoices(kind: CampaignGoalKind | null): BookingStatusChoices {
  const { completion } = goalLifecycle(kind);
  if (completion.status === "unavailable")
    return {
      question: "Is anything scheduled with this lead?",
      completion: null,
      absence: "Nothing scheduled yet",
    };
  return {
    question: completion.question,
    completion: capitalized(completion.completed),
    absence: `${capitalized(completion.missing)} yet`,
  };
}

/*
 * Where the completion controls go (docs/2026-09-23-agreed-not-booked-investigation.md, proposal
 * 2). The Booking card used to appear on every meeting-goal conversation whose offer was open, so a
 * soft no or a review hold showed "Booking" and Mark booked as its first card. The card now follows
 * agreement: it appears when the lead agreed or the booking question is open. Mark booked stays
 * reachable everywhere the offer is open, under Choose another outcome, because on a booking-link
 * campaign a lead can book through the link without ever saying yes. Checking HubSpot needs the
 * workspace's own HubSpot connection; without one the check can only fail.
 */
export type CompletionControls = {
  /** The Booking card, or null: the open booking question, or the lead's agreement. */
  card: "question" | "agreed" | null;
  /** Where Mark booked (or Mark registered) is offered: on the card, among other outcomes, or not. */
  markBooked: "card" | "other_outcomes" | null;
  /** Whether Check HubSpot booking is offered beside Mark booked. */
  checkHubSpot: boolean;
};

/** Dispositions that never take a completion: not qualified, not the lead's thread, already booked. */
const NO_COMPLETION_DISPOSITIONS: readonly WorkPlan["disposition"][] = [
  "ignored_not_qualified",
  "sender_only_cc",
  "meeting_already_confirmed",
];

/**
 * The completion controls for one conversation. `offer` is the shared `bookingOffer` decision, or
 * null while its events load; `hubSpotConnected` is whether this workspace holds a connection.
 */
export function completionControls(
  plan: WorkPlan,
  offer: { agreedAt: string | null; closedBy: string | null } | null,
  hubSpotConnected: boolean,
): CompletionControls {
  const question = openBookingQuestion(plan) !== null;
  const completionAllowed =
    tracksCompletion(plan.goalKind) &&
    offer !== null &&
    offer.closedBy === null &&
    !NO_COMPLETION_DISPOSITIONS.includes(plan.disposition);
  const agreed = completionAllowed && offer.agreedAt !== null;
  const card = question ? "question" : agreed ? "agreed" : null;
  const markBooked = !completionAllowed ? null : card === null ? "other_outcomes" : "card";
  return {
    card,
    markBooked,
    checkHubSpot:
      markBooked !== null &&
      plan.goalKind === "meeting" &&
      plan.disposition === "active" &&
      hubSpotConnected,
  };
}
